"use client";

import { useOpTheme } from "./theme";

/**
 * Переключатель темы — ОДИН на все поверхности (шапка консоли и меню профиля
 * на десктопе).
 *
 * Референс Марата — пилюля с ездящей шайбой и значками солнца и месяца. Взят
 * вид, а не код: присланный компонент держал тему в своём `useState` (то есть
 * ничего не переключал), красился хардкодом мимо токенов и тянул
 * `lucide-react`, которого в проекте нет — значки у нас свои, SVG на
 * currentColor.
 *
 * ⚠️ ДВЕ позиции, не три. «Системная» убрана по правке Марата 08.08. Режим
 * `auto` из кода НЕ выпилен: он остался в типе и в провайдере (следит за
 * настройкой ОС), просто выбрать его больше нельзя. Если у кого-то он уже
 * сохранён, подсвечивается тот вариант, в который он сейчас разрешился, —
 * иначе шайба стояла бы на «светлой» при тёмном экране.
 *
 * ⚠️ `data-theme-knob` на шайбе — НЕ украшение. Провайдер на кадр смены темы
 * гасит все переходы в приложении (иначе цвета «залипают» на старых значениях
 * var), и шайба попадала под свой же глушитель: ехать ей полагалось ровно в
 * тот кадр, когда переходы выключены. По этому атрибуту она из глушителя
 * исключена.
 */

// Ширина слота, px. Равные слоты → шайба ездит по формуле, без измерения DOM:
// у значков нет подписей, разъехаться нечему.
const SLOT = 36;

const OPTS = [
  {
    k: "light" as const,
    label: "Светлая",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
      </svg>
    ),
  },
  {
    k: "dark" as const,
    label: "Тёмная",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
      </svg>
    ),
  },
];

export function ThemeToggle({ className }: { className?: string }) {
  const { resolved, setTheme } = useOpTheme();
  // Ориентируемся на РАЗРЕШЁННУЮ тему, а не на выбранную: у сохранившегося
  // `auto` выбранного значения среди двух слотов просто нет.
  const idx = resolved === "dark" ? 1 : 0;

  return (
    <div
      className={`op-inset relative inline-flex shrink-0 rounded-full p-1 ${className ?? ""}`}
      role="group"
      aria-label="Тема оформления"
    >
      <span
        aria-hidden
        data-theme-knob
        className="op-glow-accent pointer-events-none absolute left-1 top-1 h-9 w-9 rounded-full bg-[var(--op-lime)] transition-transform duration-300"
        style={{
          transform: `translateX(${idx * SLOT}px)`,
          transitionTimingFunction: "var(--op-ease-out)",
        }}
      />
      {OPTS.map((o, i) => (
        <button
          key={o.k}
          type="button"
          onClick={() => setTheme(o.k)}
          aria-label={o.label}
          aria-pressed={idx === i}
          title={o.label}
          className={[
            "group relative z-10 grid h-9 w-9 place-items-center rounded-full outline-none transition-colors duration-300",
            "focus-visible:ring-2 focus-visible:ring-[var(--op-ring)]",
            // Значок на шайбе — БЕЛЫЙ: правило системы «всё, залитое оранжевым,
            // подписано белым» действует и здесь. Шайба оранжевая в обеих
            // темах (правка Марата 08.08), поэтому и цвет значка один.
            idx === i
              ? "text-white"
              : "text-[var(--op-dim)] hover:text-[var(--op-fg)]",
          ].join(" ")}
        >
          {/* Значок активного слегка подрастает, неактивный — мельче обычного:
              шайба «несёт» свой значок, соседний отступает на второй план. */}
          <span
            className={[
              "transition-transform duration-300 [transition-timing-function:var(--op-ease-out)]",
              idx === i ? "scale-110" : "scale-90 group-hover:scale-100",
            ].join(" ")}
          >
            {o.icon}
          </span>
        </button>
      ))}
    </div>
  );
}
